import { createContext, ReactElement, useContext, useState } from 'react'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { cn } from '@/common/utilities.ts'
import {
  CheckCircleIcon,
  CloseIcon,
  InfoCircleIcon,
  ExclamationCircleIcon,
} from '@/common/icons/general.tsx'
import { Button } from '@/app/common/components/button/Button'

type AlertResult = { canceled: boolean; confirmed: boolean; denied: boolean }

type AppAlertContextProps = {
  fireAlert: (props: AlertOptions) => Promise<AlertResult>
}

const AppAlertContext = createContext<AppAlertContextProps | undefined>(
  undefined
)

type ConfigurableOptions = {
  title?: boolean
  confirmBtn?: boolean
  denyBtn?: boolean
  titleText?: string
  confirmBtnText?: string
  denyBtnText?: string
  message?: string
  swapButtons?: boolean
  messageComponent?: React.ReactNode
}

export type VariantType = 'success' | 'info' | 'warning' | 'error' | 'confirm'

type AlertOptions = {
  variant: VariantType
} & ConfigurableOptions

type ResolverType = (value: AlertResult) => void

const AlertDefaults: { [key in VariantType]: ConfigurableOptions } = {
  success: {
    title: true,
    confirmBtn: true,
    denyBtn: false,
    titleText: 'Success',
    confirmBtnText: 'Ok',
    denyBtnText: '',
    swapButtons: false,
  },
  info: {
    title: false,
    confirmBtn: true,
    denyBtn: false,
    titleText: '',
    confirmBtnText: 'Ok',
    denyBtnText: '',
    swapButtons: false,
  },
  confirm: {
    title: true,
    confirmBtn: true,
    denyBtn: true,
    titleText: 'Warning',
    confirmBtnText: 'Yes',
    denyBtnText: 'No',
    swapButtons: true,
  },
  warning: {
    title: true,
    confirmBtn: true,
    denyBtn: false,
    titleText: 'Warning',
    confirmBtnText: 'Ok',
    denyBtnText: '',
    swapButtons: false,
  },
  error: {
    title: true,
    confirmBtn: true,
    denyBtn: false,
    titleText: 'Error',
    confirmBtnText: 'Ok',
    denyBtnText: '',
    swapButtons: false,
  },
} as const

export const AlertColors: {
  bg: { [key in VariantType]: string }
  text: { [key in VariantType]: string }
} = {
  bg: {
    success: 'bg-green-700',
    info: 'bg-blue-700',
    confirm: 'bg-yellow-600',
    warning: 'bg-yellow-600',
    error: 'bg-red-600',
  },
  text: {
    success: 'text-green-700',
    info: 'text-blue-700',
    confirm: 'text-yellow-600',
    warning: 'text-yellow-600',
    error: 'text-red-600',
  },
} as const

function setDefaultOptions(userOptions: AlertOptions): AlertOptions {
  return { ...AlertDefaults[userOptions.variant], ...userOptions }
}

type AlertButtonsProps = {
  options: AlertOptions
  onConfirm: () => void
  onDeny: () => void
}
function AlertButtons({
  options,
  onDeny,
  onConfirm,
}: AlertButtonsProps): ReactElement {
  if (options.swapButtons) {
    return (
      <>
        {options.confirmBtn && (
          <Button
            variant="primary"
            size="md"
            className="w-1/3 text-base"
            onClick={onConfirm}
          >
            {options.confirmBtnText}
          </Button>
        )}
        {options.denyBtn && (
          <Button
            variant="outline"
            size="md"
            className="w-1/3 text-base"
            onClick={onDeny}
          >
            {options.denyBtnText}
          </Button>
        )}
      </>
    )
  }

  return (
    <>
      {options.denyBtn && (
        <Button
          variant="outline"
          size="md"
          className="w-1/3 text-base"
          onClick={onDeny}
        >
          {options.denyBtnText}
        </Button>
      )}
      {options.confirmBtn && (
        <Button
          variant="primary"
          size="md"
          className="w-1/3 text-base"
          onClick={onConfirm}
        >
          {options.confirmBtnText}
        </Button>
      )}
    </>
  )
}

export function AlertIcon({ variant }: { variant: VariantType }) {
  switch (variant) {
    case 'success':
      return (
        <CheckCircleIcon className={`text-7xl ${AlertColors.text[variant]}`} />
      )
    case 'error':
      return (
        <ExclamationCircleIcon
          className={`text-7xl ${AlertColors.text[variant]}`}
        />
      )
    case 'info':
      return (
        <InfoCircleIcon className={`text-7xl ${AlertColors.text[variant]}`} />
      )
    default:
      return (
        <ExclamationCircleIcon
          className={`text-7xl ${AlertColors.text[variant]}`}
        />
      )
  }
}

export const AppAlertProvider = ({ children }: ChildrenProp) => {
  const [alertOptions, setAlertOptions] = useState<AlertOptions | null>(null)
  const [promiseResolver, setPromiseResolver] = useState<ResolverType | null>(
    null
  )
  const fireAlert = (options: AlertOptions) => {
    setAlertOptions(setDefaultOptions(options))
    return new Promise<AlertResult>((resolve) => {
      setPromiseResolver(() => resolve)
    })
  }

  const handleCancel = () => {
    if (promiseResolver)
      promiseResolver({ canceled: true, confirmed: false, denied: false })
    setAlertOptions(null)
  }

  const handleConfirm = () => {
    if (promiseResolver)
      promiseResolver({ canceled: false, confirmed: true, denied: false })
    setAlertOptions(null)
  }

  const handleDeny = () => {
    if (promiseResolver)
      promiseResolver({ canceled: false, confirmed: false, denied: true })
    setAlertOptions(null)
  }

  return (
    <AppAlertContext.Provider value={{ fireAlert }}>
      {children}
      {alertOptions && (
        <Dialog open={true} onClose={() => {}} className="relative z-[100] ">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto pointer-events-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-lg bg-white pb-4 text-center shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-sm data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                {/* Banner */}
                <div
                  className={cn(
                    `w-full h-2 ${AlertColors.bg[alertOptions.variant]}`
                  )}
                ></div>

                {/* Close button */}
                <div
                  className="flex justify-end pr-2 pt-2 text-gray-400 cursor-pointer"
                  onClick={handleCancel}
                >
                  <CloseIcon />
                </div>

                {/* Icon */}
                <AlertIcon variant={alertOptions.variant} />

                {/* Title */}
                <DialogTitle
                  className={cn(
                    'text-lg font-semibold leading-6 text-gray-900 pt-2 pb-6'
                  )}
                >
                  {alertOptions.title && alertOptions.titleText}
                </DialogTitle>

                {/* Message */}
                {!alertOptions.messageComponent && (
                  <div className="flex justify-center items-center mx-auto pb-12 text-lg pt-2">
                    <p>{alertOptions.message}</p>
                  </div>
                )}

                {/* Dynamic MessageComponent */}
                {alertOptions.messageComponent && (
                  <div className="mx-auto pb-12 text-lg pt-2 px-4 text-gray-700">
                    {alertOptions.messageComponent}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex justify-center gap-2">
                  <AlertButtons
                    options={alertOptions}
                    onConfirm={handleConfirm}
                    onDeny={handleDeny}
                  />
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}
    </AppAlertContext.Provider>
  )
}

// eslint-disable-next-line
export const useAppAlert = () => {
  const context = useContext(AppAlertContext)
  if (!context) {
    throw new Error('useAppAlertConfigs must be used within AppAlertProvider')
  }
  return context
}
